import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store/index";

import {
  getRiskKycDocumentsList,
  handleClearList,
  handlePaginate,
  reviewRiskKycDocument,
} from "store/risk/kycDocuments/slice";

import { playersListLimits } from "configs/limits";
import { usePermissions } from "helpers/usePermission";
import KycDocumentsFilter from "./components/KycDocumentsFilter";
import Pagination from "components/Pagination";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { useContentPreviewModal } from "modals/shared/ContentPreviewModal/useContentPreviewModal";
import { useTranslation } from "react-i18next";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { handleApiError } from "helpers/handleApiError";
import { showNotification, NotificationTypes } from "store/notifications/slice";

const reviewStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "expired", label: "Expired" },
  { value: "requiresReview", label: "Requires Review" },
];

const KYC_STATUS_DISPLAY: Record<
  string,
  { label: string; badgeStatus: string }
> = {
  none: { label: "Not Started", badgeStatus: "none" },
  missing: { label: "Required", badgeStatus: "warning" },
  pending: { label: "Under Review", badgeStatus: "pending" },
  approved: { label: "Approved", badgeStatus: "approved" },
  requiresreview: { label: "Needs Review", badgeStatus: "warning" },
  requiresreupload: { label: "Action Required", badgeStatus: "warning" },
  rejected: { label: "Rejected", badgeStatus: "rejected" },
  expired: { label: "Expired", badgeStatus: "expired" },
};

const RiskKycDocumentsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [currentLimit, setLimit] = useState<number>(playersListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [searchId, setSearchId] = useState<string>("");
  const [selectedDocumentStatuses, setSelectedDocumentStatuses] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [selectedDocumentSides, setSelectedDocumentSides] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [selectedSources, setSelectedSources] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<{
    id: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    documentType: string;
    documentSide: string;
  }>({
    id: "desc",
    createdAt: "desc",
    updatedAt: "none",
    status: "none",
    documentType: "none",
    documentSide: "none",
  });

  const list = useAppSelector((store) => store.riskKycDocuments.list);

  const isListFetched = useAppSelector(
    (store) => store.riskKycDocuments.isListFetched,
  );

  const totalItems = useAppSelector(
    (store) => store.riskKycDocuments.totalItems,
  );

  const totalPages = useAppSelector(
    (store) => store.riskKycDocuments.totalPages,
  );

  const activePage = useAppSelector(
    (store) => store.riskKycDocuments.activePage,
  );

  const processingDocumentId = useAppSelector(
    (store) => store.riskKycDocuments.processingDocumentId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: "riskKycDocuments",
    isListFetched,
  });

  const activePermissions = usePermissions("risk_kyc_documents");
  const canUpdate = activePermissions?.includes("update") ?? false;
  const { openPreview } = useContentPreviewModal();

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getRiskKycDocumentsList({
          limit: currentLimit,
          offset: activePage - 1,

          email: search || undefined,

          userIds: searchId ? [Number(searchId)] : undefined,

          status:
            selectedDocumentStatuses.length > 0
              ? selectedDocumentStatuses.map((el) => String(el.value))
              : undefined,

          documentType:
            selectedDocumentTypes.length > 0
              ? selectedDocumentTypes.map((el) => String(el.value))
              : undefined,
          documentSide:
            selectedDocumentSides.length > 0
              ? String(selectedDocumentSides[0].value)
              : undefined,

          source:
            selectedSources.length > 0
              ? String(selectedSources[0].value)
              : undefined,

          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  useEffect(() => {
    dispatch(handleClearList());
    if (activePage !== 1) {
      dispatch(handlePaginate(1));
    }

    if (!activePermissions?.includes("read") && activePermissions !== null) {
      navigate("/dashboard");
    }
  }, [location.pathname]);

  const renderStatus = (value?: string | null, isRiskLevel = false) => {
    const statusKey = value?.toLowerCase() ?? "none";
    const statusDisplay = KYC_STATUS_DISPLAY[statusKey];

    return (
      <span
        className={`badge badge--${
          statusDisplay?.badgeStatus ?? statusKey
        } kyc-documents-status-badge${
          isRiskLevel ? " kyc-documents-status-badge--risk-level" : ""
        }`}
      >
        {statusDisplay?.label ?? value ?? "Not Started"}
      </span>
    );
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];

    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };

    // @ts-ignore
    setOrder(newOrder);

    dispatch(handlePaginate(1));
  };

  const formattedList = list.map((item) => ({
    id: item.id,
    email: item.email,
    userId: item.userId,
    documentType: item.documentType,
    documentSide: item.documentSide,
    source: item.source,
    fileName: item.fileName,
    fileSize: item.fileSize,
    kycStatus: item.kycStatus,
    riskLevel: item.riskLevel,
    reviewerEmail: item.reviewerEmail,
    reviewerId: item.reviewerId,
    mimeType: item.mimeType,
    createdAt: item.createdAt,
    status: item.status,
    updatedAt: item.updatedAt,
  }));

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <KycDocumentsFilter
        searchValue={search}
        handleSearch={setSearch}
        searchId={searchId}
        handleSearchId={setSearchId}
        selectedDocumentStatuses={selectedDocumentStatuses}
        handleUpdateDocumentStatuses={setSelectedDocumentStatuses}
        selectedDocumentTypes={selectedDocumentTypes}
        handleUpdateDocumentTypes={setSelectedDocumentTypes}
        selectedSources={selectedSources}
        selectedDocumentSides={selectedDocumentSides}
        handleUpdateDocumentSides={setSelectedDocumentSides}
        handleUpdateSources={setSelectedSources}
        handleApply={() => {
          dispatch(handleClearList());
          dispatch(handlePaginate(1));
        }}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box"></div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={12} rowsCount={10} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 && (
              <EmptyState message={t("noKycDocumentsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={formattedList}
                order={order}
                idField="id"
                hasCheckbox={false}
                hasDelete={false}
                hasEdit={false}
                hasView
                processingId={processingDocumentId}
                onView={(id) => {
                  const document = list.find((item) => item.id === Number(id));
                  if (document) openPreview(document.userId, document.id);
                }}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                columns={{
                  hiddenColumns: [
                    "reviewerId",
                    "mimeType",
                    "createdAt",
                    "fileName",
                    "fileSize",
                  ],

                  sortingColumns: {
                    id: {
                      value: t("id"),
                      hasSorting: true,
                    },

                    userId: {
                      value: t("userId"),
                    },

                    email: {
                      value: t("player"),
                    },

                    documentType: {
                      value: t("kycDocumentColumn"),
                      hasSorting: true,
                    },

                    documentSide: {
                      value: t("side"),
                      hasSorting: true,
                    },

                    source: {
                      value: t("source"),
                    },

                    fileName: {
                      value: t("fileName"),
                    },

                    fileSize: {
                      value: t("fileSize"),
                    },

                    kycStatus: {
                      value: t("kycStatus"),
                    },

                    riskLevel: {
                      value: t("riskLevel"),
                    },

                    reviewerEmail: {
                      value: t("reviewedBy"),
                    },

                    status: {
                      value: t("status"),
                      hasSorting: true,
                    },

                    updatedAt: {
                      value: t("updatedAt"),
                      hasSorting: true,
                    },
                  },
                }}
                customColumns={(row) => ({
                  fileName: (
                    <div className="kyc-file-name" title={row.fileName}>
                      {row.fileName}
                    </div>
                  ),
                  fileSize: (
                    <>
                      {row.fileSize < 1024 * 1024
                        ? `${(row.fileSize / 1024).toFixed(1)} KB`
                        : `${(row.fileSize / 1024 / 1024).toFixed(1)} MB`}
                    </>
                  ),
                  status: (
                    <div
                      className={`table-status-dropdown table-status-dropdown--kyc table-status-dropdown--${row.status?.toLowerCase()}`}
                    >
                      <DefaultDropdown
                        value={row.status}
                        options={reviewStatusOptions}
                        isSmall
                        useFixedMenu
                        disabled={!canUpdate}
                        onSelect={async (status) => {
                          if (status === row.status) return;
                          try {
                            await dispatch(
                              reviewRiskKycDocument({
                                documentId: row.id,
                                status: status as
                                  | "pending"
                                  | "approved"
                                  | "rejected"
                                  | "expired"
                                  | "requiresReview",
                              }),
                            ).unwrap();
                            dispatch(
                              showNotification({
                                title: t("success"),
                                message: t("entityUpdateSuccess"),
                                type: NotificationTypes.success,
                              }),
                            );
                          } catch (error) {
                            handleApiError({ error, t, dispatch });
                          }
                        }}
                      />
                    </div>
                  ),
                  riskLevel: renderStatus(row.riskLevel, true),
                  kycStatus: renderStatus(row.kycStatus),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);

              dispatch(handlePaginate(1));
            }}
            limitsArray={playersListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default RiskKycDocumentsList;
